import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal, X, Search, ChevronDown, ChevronRight } from 'lucide-react';
import {
  getAllProducts,
  getAllCategories,
  getProductsByCategory,
  getProductsByClass,
  searchProducts,
} from '../api/productAPI';
import axiosInstance from '../api/axiosInstance';
import ProductCard from '../components/ProductCard';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './ShopPage.css';

const ShopPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCats, setExpandedCats] = useState({});

  const categoryParam = searchParams.get('category');
  const subcategoryParam = searchParams.get('subcategory');
  const classParam = searchParams.get('class');
  const searchParam = searchParams.get('search');

  useEffect(() => { fetchCategories(); }, []);
  useEffect(() => { fetchProducts(); }, [categoryParam, subcategoryParam, classParam, searchParam]);

  const fetchCategories = async () => {
    try {
      const response = await getAllCategories();
      if (response.success) setCategories(response.data);
    } catch (error) {}
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      let response;
      if (searchParam) {
        response = await searchProducts(searchParam);
      } else if (subcategoryParam) {
        // Filter by subcategory
        const res = await axiosInstance.get(`/products?subcategoryId=${subcategoryParam}`);
        response = res.data;
      } else if (categoryParam) {
        response = await getProductsByCategory(categoryParam);
      } else if (classParam) {
        response = await getProductsByClass(classParam);
      } else {
        response = await getAllProducts();
      }
      if (response.success) setProducts(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) setSearchParams({ search: searchQuery });
  };

  const handleCategoryFilter = (categoryId) => {
    setSearchParams({ category: categoryId });
    // Auto-expand the selected category to show subcategories
    setExpandedCats(prev => ({ ...prev, [categoryId]: true }));
  };

  const handleSubcategoryFilter = (subcategoryId, parentCategoryId) => {
    setSearchParams({ category: parentCategoryId, subcategory: subcategoryId });
  };

  const handleClassFilter = (productClass) => {
    setSearchParams({ class: productClass });
  };

  const clearFilters = () => {
    setSearchParams({});
    setSearchQuery('');
    setExpandedCats({});
  };

  const toggleCatExpand = (catId) => {
    setExpandedCats(prev => ({ ...prev, [catId]: !prev[catId] }));
  };

  const getActiveFilter = () => {
    if (searchParam) return `Search: "${searchParam}"`;
    if (subcategoryParam) {
      // Find subcategory name
      for (const cat of categories) {
        const sub = cat.subcategories?.find(s => s.id === parseInt(subcategoryParam));
        if (sub) return `${cat.name} › ${sub.name}`;
      }
      return 'Subcategory';
    }
    if (categoryParam) {
      const cat = categories.find(c => c.id === parseInt(categoryParam));
      return cat ? cat.name : 'Category';
    }
    if (classParam) return `${classParam === 'FIRST' ? 'First Class' : classParam.charAt(0) + classParam.slice(1).toLowerCase()}`;
    return null;
  };

  const activeFilter = getActiveFilter();
  // Top-level categories only
  const topLevelCats = categories.filter(c => !c.parent);

  return (
    <>
      <Navbar />
      <div className="shop-page">
        <div className="container">
          <div className="shop-header">
            <div>
              <h1 className="section-title">Our Products</h1>
              <p className="section-subtitle">
                {products.length} product{products.length !== 1 ? 's' : ''} available
              </p>
            </div>
            <div className="shop-header-actions">
              <form onSubmit={handleSearch} className="shop-search">
                <Search size={16} />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="shop-search-input"
                />
              </form>
              <button className="filter-toggle-btn" onClick={() => setFiltersOpen(!filtersOpen)}>
                <SlidersHorizontal size={16} /> Filters
              </button>
            </div>
          </div>

          {activeFilter && (
            <div className="active-filter">
              <span>Showing: <strong>{activeFilter}</strong></span>
              <button onClick={clearFilters}><X size={14} /> Clear</button>
            </div>
          )}

          <div className="shop-layout">
            <aside className={`shop-sidebar ${filtersOpen ? 'open' : ''}`}>

              {/* Product Class */}
              <div className="sidebar-section">
                <h4 className="sidebar-title">Product Class</h4>
                <div className="filter-options">
                  {['ECONOMY', 'BUSINESS', 'FIRST'].map((cls) => (
                    <button
                      key={cls}
                      className={`filter-option ${classParam === cls ? 'active' : ''}`}
                      onClick={() => handleClassFilter(cls)}
                    >
                      {cls === 'FIRST' ? 'First Class' : cls.charAt(0) + cls.slice(1).toLowerCase()}
                    </button>
                  ))}
                </div>
              </div>

              {/* Categories with Subcategories */}
              {topLevelCats.length > 0 && (
                <div className="sidebar-section">
                  <h4 className="sidebar-title">Categories</h4>
                  <div className="filter-options">
                    {topLevelCats.map((category) => {
                      const isActive = categoryParam === String(category.id) && !subcategoryParam;
                      const hasSubcats = category.subcategories?.length > 0;
                      const isExpanded = expandedCats[category.id];

                      return (
                        <div key={category.id} className="category-filter-group">
                          <div className="category-filter-row">
                            <button
                              className={`filter-option filter-option-cat ${isActive ? 'active' : ''}`}
                              onClick={() => handleCategoryFilter(category.id)}
                            >
                              {category.name}
                            </button>
                            {hasSubcats && (
                              <button
                                className="subcat-toggle"
                                onClick={() => toggleCatExpand(category.id)}
                              >
                                {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                              </button>
                            )}
                          </div>

                          {/* Subcategories */}
                          {hasSubcats && isExpanded && (
                            <div className="subcategory-list">
                              {category.subcategories.map((sub) => {
                                const isSubActive = subcategoryParam === String(sub.id);
                                return (
                                  <button
                                    key={sub.id}
                                    className={`filter-option filter-option-sub ${isSubActive ? 'active' : ''}`}
                                    onClick={() => handleSubcategoryFilter(sub.id, category.id)}
                                  >
                                    ↳ {sub.name}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <button className="clear-filters-btn" onClick={clearFilters}>
                Clear All Filters
              </button>
            </aside>

            <div className="shop-products">
              {loading ? (
                <div className="shop-grid">
                  {[...Array(8)].map((_, i) => <div key={i} className="product-skeleton" />)}
                </div>
              ) : products.length > 0 ? (
                <div className="shop-grid">
                  {products.map((product) => <ProductCard key={product.id} product={product} />)}
                </div>
              ) : (
                <div className="empty-state">
                  <Search size={48} color="#29ABE2" strokeWidth={1} />
                  <h3>No products found</h3>
                  <p>Try adjusting your filters or search query</p>
                  <button onClick={clearFilters} className="btn-primary">Clear Filters</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ShopPage;